import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Settings, Building2, User, Palette } from 'lucide-react';
import { PricingConfig } from './admin/PricingConfig';
import { CompanySettings } from './admin/CompanySettings';
import { AccountSettings } from './admin/AccountSettings';
import { CustomizationSettings } from './admin/CustomizationSettings';
import { SubscriptionBanner } from './admin/SubscriptionBanner';
import { useUserData } from '../hooks/useUserData';
import { useSubscription } from '../hooks/useSubscription';

export function AdminPanel() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('pricing');
  const { userData, setUserData, loading, error } = useUserData(user?.uid || '');
  const { features } = useSubscription();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  const calculatorUrl = `${window.location.origin}/${userData.username}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="card rounded-lg shadow-lg">
        <div className="px-4 py-5 border-b border-[var(--color-border)] sm:px-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-default">Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-muted">
                Your calculator: <a href={calculatorUrl} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{calculatorUrl}</a>
              </div>
              <button onClick={logout} className="btn-secondary">Logout</button>
            </div>
          </div>
        </div>

        <SubscriptionBanner />

        <div className="border-b border-[var(--color-border)]">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('pricing')}
              className={`px-6 py-3 font-medium text-sm border-b-2 ${
                activeTab === 'pricing'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-default hover:border-gray-300'
              } flex items-center`}
            >
              <Settings className="w-4 h-4 mr-2" />
              Pricing Configuration
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`px-6 py-3 font-medium text-sm border-b-2 ${
                activeTab === 'company'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-default hover:border-gray-300'
              } flex items-center`}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Company Settings
            </button>
            <button
              onClick={() => setActiveTab('account')}
              className={`px-6 py-3 font-medium text-sm border-b-2 ${
                activeTab === 'account'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-default hover:border-gray-300'
              } flex items-center`}
            >
              <User className="w-4 h-4 mr-2" />
              Account Settings
            </button>
            {features.customColors && (
              <button
                onClick={() => setActiveTab('customization')}
                className={`px-6 py-3 font-medium text-sm border-b-2 ${
                  activeTab === 'customization'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted hover:text-default hover:border-gray-300'
                } flex items-center`}
              >
                <Palette className="w-4 h-4 mr-2" />
                Customization
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'pricing' && (
            <PricingConfig
              config={userData.pricingConfig}
              onUpdate={(config) => setUserData({ ...userData, pricingConfig: config })}
            />
          )}
          
          {activeTab === 'company' && (
            <CompanySettings
              settings={userData.settings}
              onUpdate={(settings) => setUserData({ ...userData, settings })}
            />
          )}

          {activeTab === 'account' && (
            <AccountSettings
              username={userData.username}
              onUpdate={(username) => setUserData({ ...userData, username })}
            />
          )}

          {activeTab === 'customization' && features.customColors && (
            <CustomizationSettings
              theme={userData.customization?.theme || {
                primary: '#0369a1',
                secondary: '#1e293b',
                accent: '#16a34a',
                background: '#ffffff',
                text: '#1a1a1a',
                muted: '#6b7280',
                border: '#e5e7eb'
              }}
              domain={userData.customization?.domain || { verified: false }}
              onUpdate={(updates) => setUserData({
                ...userData,
                customization: {
                  ...userData.customization,
                  ...updates
                }
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
}